'use client'

import type { QueryClient } from '@tanstack/react-query'
import { lazy, Suspense, useEffect, useState } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import { env } from '~/env'
import { createQueryClient } from './query-client'

let clientQueryClientSingleton: QueryClient | undefined = undefined
const getQueryClient = () => {
  if (typeof window === 'undefined') {
    // Server: always make a new query client
    return createQueryClient()
  } else {
    // Browser: use singleton pattern to keep the same query client
    return (clientQueryClientSingleton ??= createQueryClient())
  }
}

const ReactQueryDevtoolsProduction = lazy(() =>
  import('@tanstack/react-query-devtools/production').then((d) => ({
    default: d.ReactQueryDevtools,
  })),
)

const isDevOrStaging = ['development', 'staging'].includes(
  // eslint-disable-next-line no-restricted-properties
  env.NEXT_PUBLIC_PROD_ENV ?? process.env.NEXT_PUBLIC_PROD_ENV ?? '',
)

export function ReactQueryProvider(props: { children: React.ReactNode }) {
  const queryClient = getQueryClient()

  const [showDevtools, setShowDevtools] = useState(isDevOrStaging)

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    window.toggleDevtools = () => setShowDevtools((old) => !old)
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      {props.children}

      {isDevOrStaging && <ReactQueryDevtools initialIsOpen={false} />}

      {showDevtools && (
        <Suspense fallback={null}>
          <ReactQueryDevtoolsProduction />
        </Suspense>
      )}
    </QueryClientProvider>
  )
}
